import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Building2, IndianRupee, CreditCard, 
  Crown, Zap, Plus, ExternalLink, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { useWorkspace } from '../context/WorkspaceContext';
import { AICreditsCard } from '../components/billing/AICreditsCard';
import { AddonsCard } from '../components/billing/AddonsCard';

const API = API_BASE;

const Billing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace() || {};
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      
      // Fetch workspace purchases
      const purchasesRes = await axios.get(`${API}/razorpay/workspace/purchases`, {
        headers: getAuthHeaders()
      });
      setPurchases(purchasesRes.data || []);
      
      // Fetch billing info for transactions (if available)
      try {
        const billingRes = await axios.get(`${API}/user/billing`, {
          headers: getAuthHeaders()
        });
        setTransactions(billingRes.data?.transactions || []);
      } catch (e) {
        // Billing endpoint might not exist yet
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(Math.round(price));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getWorkspaceTypeIcon = (type) => {
    return type === 'studio' ? Crown : Zap;
  };

  const getWorkspaceTypeColor = (type) => {
    return type === 'studio' ? 'text-amber-500' : 'text-blue-500';
  };

  const getWorkspaceTypeBg = (type) => {
    return type === 'studio' ? 'bg-amber-500/10' : 'bg-blue-500/10';
  };

  const activePurchases = purchases.filter(p => p.payment_status === 'paid' && p.is_active);
  const pendingPurchases = purchases.filter(p => p.payment_status === 'paid' && !p.workspace_id);

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">Billing</h1>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-muted-foreground">Loading billing information...</div>
          </div>
        ) : (
          <>
            {/* Pending Purchases Alert */}
            {pendingPurchases.length > 0 && (
              <Card className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-amber-800 dark:text-amber-200">
                      You have {pendingPurchases.length} unused workspace purchase{pendingPurchases.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Create a workspace to start using your subscription
                    </p>
                  </div>
                  <Button asChild size="sm">
                    <Link to={`/workspace/create?purchase_id=${pendingPurchases[0].id}`}>
                      Create Workspace
                    </Link>
                  </Button>
                </div>
              </Card>
            )}

            {/* Current workspace usage & add-ons (Phase 2) */}
            {currentWorkspace?.id && (
              <section className="mb-8" data-testid="billing-workspace-usage-section">
                <h2 className="text-xl font-bold mb-4">
                  Usage &amp; add-ons · {currentWorkspace.name}
                </h2>
                <div className="space-y-4">
                  <AICreditsCard
                    workspaceId={currentWorkspace.id}
                    workspaceName={currentWorkspace.name}
                  />
                  <AddonsCard workspaceId={currentWorkspace.id} />
                </div>
              </section>
            )}

            {/* Active Workspace Subscriptions */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Workspace Subscriptions</h2>
                <Button asChild variant="outline" size="sm">
                  <Link to="/workspace/purchase">
                    <Plus className="h-4 w-4 mr-2" />
                    Buy Workspace
                  </Link>
                </Button>
              </div>

              {activePurchases.length === 0 ? (
                <Card className="p-8 text-center">
                  <div className="text-muted-foreground mb-4">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No paid workspace subscriptions yet</p>
                    <p className="text-sm mt-2">You have a free workspace. Purchase Pro or Studio Workspaces for additional features.</p>
                  </div>
                  <Button asChild>
                    <Link to="/workspace/purchase">
                      <Crown className="h-4 w-4 mr-2" />
                      Buy a Workspace
                    </Link>
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  {activePurchases.map((purchase) => {
                    const Icon = getWorkspaceTypeIcon(purchase.workspace_type);
                    const color = getWorkspaceTypeColor(purchase.workspace_type);
                    const bgColor = getWorkspaceTypeBg(purchase.workspace_type);
                    
                    return (
                      <Card key={purchase.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${bgColor}`}>
                              <Icon className={`h-6 w-6 ${color}`} />
                            </div>
                            <div>
                              <p className="font-semibold capitalize">
                                {purchase.workspace_type} Workspace
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {purchase.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} • 
                                Renews {formatDate(purchase.current_period_end)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold flex items-center gap-1">
                              <IndianRupee className="h-4 w-4" />
                              {formatPrice(purchase.amount)}
                              <span className="text-muted-foreground font-normal">
                                /{purchase.billing_cycle === 'yearly' ? 'yr' : 'mo'}
                              </span>
                            </p>
                            {purchase.workspace_id && (
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="h-auto p-0"
                                onClick={() => navigate(`/workspaces/${purchase.workspace_id}`)}
                              >
                                View Workspace <ExternalLink className="h-3 w-3 ml-1" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Payment History */}
            <section>
              <h2 className="text-xl font-bold mb-4">Payment History</h2>
              
              {purchases.length === 0 && transactions.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payment history yet</p>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <div className="divide-y divide-border">
                    {purchases.map((purchase) => (
                      <div key={purchase.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium capitalize">
                              {purchase.workspace_type} Workspace - {purchase.billing_cycle}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(purchase.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium flex items-center gap-1 justify-end">
                            <IndianRupee className="h-3 w-3" />
                            {formatPrice(purchase.amount)}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            purchase.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : purchase.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {purchase.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </section>

            {/* Payment Methods Info */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <CreditCard className="h-4 w-4" />
                Secure payments powered by Razorpay • UPI, Cards, Netbanking accepted
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Billing;
